/**
 * Push notifications, client side (the server side is src/server/app/Libraries/Push).
 *
 * The feature is optional and self-contained: if the server answers 404 (PUSH_ENABLED=false) or the
 * browser cannot do Web Push, `available` stays false and the section is not rendered.
 *
 * Note on the devices: "browser" and "mobile" are the same Web Push channel. What tells them apart
 * is the device a subscription belongs to, decided here when the browser subscribes for the first
 * time (see `deviceType()`), and the server keeps one row per device.
 */
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMainStore } from '@/stores/main'
import * as Alerts from '@/services/Alerts'
import * as Server from '@/services/Server'

export function usePush() {
  const store = useMainStore()
  const { t } = useI18n()

  // Where this browser keeps the id the server gave to its own device, so it can tell whether it
  // is still receiving anything (see `load`).
  const STORAGE_KEY = 'push:deviceId'

  // Feature available on the server and loaded
  const available = ref(false)
  const loading = ref(true)
  const saving = ref(false)
  const activating = ref(false)

  // What the server offers
  const channels = ref([])
  const rows = ref([])
  const options = ref({ minIntervalMinutes: [], maxPerDay: [], defaults: {} })
  const preferences = ref({ minIntervalMinutes: null, maxPerDay: null })
  const subscriptions = ref([])
  const vapidPublicKey = ref('')

  // This browser
  const supported = ref(false)
  // Web Push exists only on a secure origin: an https page with a certificate the browser trusts,
  // or localhost. Over http - or on a page whose certificate warning was clicked through - the
  // browser refuses with "The operation is insecure" and there is nothing the page can do about it.
  const insecure = window.isSecureContext === false
  // The permission this browser has for the site. "denied" is the silent killer: the push leaves,
  // the push service accepts it, and the browser drops it - the server cannot know, so the page
  // has to be the one telling the user.
  const blocked = ref(false)
  // Why the file of the service worker did not load: 'blocked' (a filter refused the request),
  // 'missing' (not uploaded), 'not-js' (served as a page, e.g. the index.html fallback).
  const serviceWorkerIssue = ref(null)
  // This device had a subscription and does not have one anymore.
  const deviceLost = ref(false)
  const thisDeviceActive = ref(false)

  const isMobile = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent)
    || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true

  /** iOS delivers the Web Push only to a site opened from the home screen. */
  const needsHomeScreen = computed(() => isIOS && !isStandalone)

  const isDesktop = computed(() => !isMobile)
  const emailVerified = computed(() => !!store.user?.email_verified_at)

  /**
   * The page to open on the phone. It is the address the user is on right now, so it is right
   * whatever path the site is published under.
   */
  const settingsUrl = computed(() => window.location.origin + window.location.pathname)

  /** How this device is called in the list of the activated ones. */
  const deviceLabel = computed(() => browserName() + ' - ' + osName())

  /** The frequency choices, ready for a select. */
  const intervalItems = computed(() => options.value.minIntervalMinutes.map(value => ({
    value,
    title: value === 0 ? t('push.frequency.noLimit') : (value < 60
      ? t('push.frequency.minutes', { n: value })
      : t('push.frequency.hours', { n: Math.round(value / 60 * 10) / 10 })),
  })))

  const perDayItems = computed(() => options.value.maxPerDay.map(value => ({
    value,
    title: value === 0 ? t('push.frequency.noLimit') : t('push.frequency.perDay', { n: value }),
  })))

  // The thresholds are shown as ">= 3": the meaning belongs to the condition, the page does not
  // have to know it (see the Condition interface on the server).
  const thresholdItems = (values) => values.map(value => ({ value, title: '>= ' + value }))

  async function load() {
    loading.value = true

    supported.value = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window

    try {
      const response = await Server.get(store.apiPath.push_settings)
      const data = response.data?.data ?? {}

      vapidPublicKey.value = data.vapidPublicKey ?? ''
      channels.value = data.channels ?? []
      rows.value = (data.conditions ?? []).map(condition => ({
        conditionKey: condition.conditionKey,
        thresholds: condition.thresholds ?? [],
        threshold: condition.threshold,
        channels: { ...condition.channels },
      }))
      options.value = data.options ?? options.value

      // The server sends the limits in force (its own defaults when the user never chose), so the
      // page has nothing to decide: it shows what it is given.
      preferences.value = {
        minIntervalMinutes: data.preferences?.minIntervalMinutes ?? null,
        maxPerDay: data.preferences?.maxPerDay ?? null,
      }
      subscriptions.value = data.subscriptions ?? []

      blocked.value = typeof Notification !== 'undefined' && Notification.permission === 'denied'

      await refreshThisDevice()

      // The id this browser stored when it activated itself: if it is not in the list anymore, or
      // the browser has lost the subscription, this device receives nothing without anybody
      // noticing. The server cannot see it either, so the page says it.
      const storedId = window.localStorage.getItem(STORAGE_KEY)

      deviceLost.value = !!storedId && !(
        thisDeviceActive.value
        && subscriptions.value.some(device => String(device.id) === storedId)
      )

      available.value = true
    } catch {
      // 404: the feature is switched off on the server. Nothing to show, nothing broken.
      available.value = false
    } finally {
      loading.value = false
    }
  }

  /** Whether this browser is already subscribed (the row on the server is written by `activate`). */
  async function refreshThisDevice() {
    if (!supported.value || !('serviceWorker' in navigator)) {
      return
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration()
      const subscription = registration ? await registration.pushManager.getSubscription() : null
      thisDeviceActive.value = !!subscription
    } catch {
      thisDeviceActive.value = false
    }
  }

  async function refreshSubscriptions() {
    try {
      const response = await Server.get(store.apiPath.push_settings)
      subscriptions.value = response.data?.data?.subscriptions ?? []
    } catch {
      // The list stays as it was: it is not worth an error message.
    }
  }

  /**
   * Activates the notifications on this device. It has to be called from a click: the permission
   * prompt is only allowed inside a user gesture.
   */
  async function activate() {
    activating.value = true

    try {
      if (window.isSecureContext === false) {
        console.error('push: this page is not a secure context (http or a certificate warning)', window.location.href)
        Alerts.error(t('push.insecure'))

        return
      }

      if (!supported.value) {
        Alerts.error(t('push.unsupported'))

        return
      }

      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        blocked.value = permission === 'denied'
        Alerts.error(t('push.permissionDenied'))

        return
      }

      // The two steps that fail for their own reasons are reported apart: the console says which
      // one gave up (a missing push-sw.js and an origin the browser distrusts look nothing alike).
      let registration

      try {
        registration = await registerServiceWorker()
      } catch (error) {
        console.error('push: the service worker was not registered', error)

        // The file is fetched to tell the reasons apart: a filter that refuses the request looks
        // like a network error, a file that was not uploaded answers 404, and one served by the
        // fallback of the site comes back as HTML.
        serviceWorkerIssue.value = await probeServiceWorkerFile()
        Alerts.error(t(serviceWorkerIssue.value === 'blocked' ? 'push.swBlocked' : 'push.swMissing'))

        return
      }

      serviceWorkerIssue.value = null

      let subscription = await registration.pushManager.getSubscription()

      if (!subscription) {
        try {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey.value),
          })
        } catch (error) {
          console.error('push: the browser refused the subscription', error)
          Alerts.error(t('push.activateError'))

          return
        }
      }

      const json = subscription.toJSON()

      const response = await Server.post(store.apiPath.push_subscriptions, {
        endpoint: json.endpoint,
        keys: json.keys,
        deviceType: deviceType(),
        label: deviceLabel.value,
      })

      // Remember which row is this device, so the next visit can notice if it disappears.
      const id = response.data?.data?.id

      if (id) {
        window.localStorage.setItem(STORAGE_KEY, String(id))
      }

      deviceLost.value = false
      thisDeviceActive.value = true
      await refreshSubscriptions()
      Alerts.success(t('push.activated'))
    } catch (error) {
      console.error('push: activation failed', error)
      Alerts.error(t('push.activateError'))
    } finally {
      activating.value = false
    }
  }

  /** The row on the server disappears: from now on this device is not written to anymore. */
  async function removeSubscription(id) {
    try {
      await Server.destroy(store.apiPath.push_subscription(id))

      // Removed by the user on purpose: no warning the next time this page is opened.
      if (window.localStorage.getItem(STORAGE_KEY) === String(id)) {
        window.localStorage.removeItem(STORAGE_KEY)
      }

      await refreshSubscriptions()
    } catch (error) {
      console.error('push: removing the device failed', error)
      Alerts.error(t('push.saveError'))
    }
  }

  /**
   * Saves the table and the frequency limits. It returns whether it worked instead of reporting it:
   * the button that calls it is on the settings page (it saves the language too), and the message
   * is its business.
   */
  async function save() {
    saving.value = true

    try {
      await Server.put(store.apiPath.push_settings, {
        // The full object: what the page shows is what the server gets.
        rules: rows.value.map(row => ({
          conditionKey: row.conditionKey,
          threshold: row.threshold,
          channels: row.channels,
        })),
        preferences: {
          minIntervalMinutes: preferences.value.minIntervalMinutes,
          maxPerDay: preferences.value.maxPerDay,
        },
      })

      return true
    } catch (error) {
      console.error('push: saving failed', error)

      return false
    } finally {
      saving.value = false
    }
  }

  /** "Send me one now": the test does not go through the rate limits (see the server). */
  async function sendTest(channel) {
    try {
      const response = await Server.post(store.apiPath.push_test, { channel })
      const sent = response.data?.data?.sent

      if (sent) {
        Alerts.success(t('push.test.sent'))
      } else {
        Alerts.warning(t('push.test.notSent'))
      }
    } catch (error) {
      console.error('push: the test notification failed', error)
      Alerts.error(t('push.saveError'))
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(settingsUrl.value)
      Alerts.success(t('push.phone.copied'))
    } catch {
      Alerts.warning(settingsUrl.value)
    }
  }

  /** The system share sheet, when the browser has one (Windows shows Phone Link, WhatsApp, ...). */
  async function shareLink() {
    try {
      await navigator.share({ title: 'GPO II', url: settingsUrl.value })
    } catch {
      // The user closed the sheet: nothing to report.
    }
  }

  const canShare = typeof navigator.share === 'function'

  async function registerServiceWorker() {
    // The file sits next to index.html, so its scope is the whole site.
    const url = import.meta.env.BASE_URL + 'push-sw.js'
    await navigator.serviceWorker.register(url, { scope: import.meta.env.BASE_URL })

    // Subscribe needs a service worker that is already in charge.
    return navigator.serviceWorker.ready
  }

  /**
   * Asks for the service worker file the way the browser does, to name the real problem: a filter
   * (an ad blocker, a privacy extension, a web shield) makes the request fail, while a file that
   * was not uploaded answers, and one served by the fallback of the site answers as a page.
   */
  async function probeServiceWorkerFile() {
    try {
      const response = await fetch(import.meta.env.BASE_URL + 'push-sw.js', { cache: 'no-store' })

      if (! response.ok) {
        return 'missing'
      }

      return (response.headers.get('content-type') ?? '').includes('javascript') ? 'ok' : 'not-js'
    } catch {
      return 'blocked'
    }
  }

  function deviceType() {
    return isMobile ? 'mobile' : 'desktop'
  }

  function browserName() {
    const ua = navigator.userAgent

    if (/Edg\//.test(ua)) return 'Edge'
    if (/OPR\//.test(ua)) return 'Opera'
    if (/SamsungBrowser/.test(ua)) return 'Samsung'
    if (/Firefox\//.test(ua)) return 'Firefox'
    if (/CriOS|Chrome\//.test(ua)) return 'Chrome'
    if (/Safari\//.test(ua)) return 'Safari'

    return 'Browser'
  }

  function osName() {
    const ua = navigator.userAgent

    if (/Windows/.test(ua)) return 'Windows'
    if (isIOS) return /iPad/.test(ua) || /Macintosh/.test(ua) ? 'iPad' : 'iPhone'
    if (/Android/.test(ua)) return 'Android'
    if (/Mac OS X/.test(ua)) return 'macOS'
    if (/Linux/.test(ua)) return 'Linux'

    return 'device'
  }

  /** The applicationServerKey has to be the base64url VAPID key as bytes. */
  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)

    for (let i = 0; i < raw.length; i++) {
      output[i] = raw.charCodeAt(i)
    }

    return output
  }

  return {
    available,
    loading,
    saving,
    activating,
    supported,
    insecure,
    blocked,
    serviceWorkerIssue,
    deviceLost,
    vapidPublicKey,
    channels,
    rows,
    preferences,
    subscriptions,
    intervalItems,
    perDayItems,
    thresholdItems,
    thisDeviceActive,
    needsHomeScreen,
    isDesktop,
    isMobile,
    emailVerified,
    deviceLabel,
    settingsUrl,
    canShare,
    load,
    activate,
    removeSubscription,
    save,
    sendTest,
    copyLink,
    shareLink,
  }
}
